import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

// Shared chrome + content primitives for Terms/Privacy/Cookie Policy - the
// same three documents as landing/templates.mjs's renderTerms/renderPrivacy/
// renderCookiePolicy, ported to this app's own design system (Tailwind,
// dark: variants) instead of the marketing site's plain-HTML/CSS templates,
// so wording stays one source of truth in spirit even though the two sites
// render it through different stacks.
export const SITE_URL = 'https://zmist.co';
export const APP_URL = 'https://app.zmist.co';
export const LEGAL_UPDATED = 'August 26, 2026';

// Real values, filled in from what Anar gave us - have the whole draft
// reviewed by an actual lawyer before treating it as binding, before
// relying on this for real users, especially any in the EU/UK (GDPR) or
// California (CCPA). No printed registered address, since a JDG has none
// to publish; contact is email-only.
export const OPERATOR = 'Anar Alishov, prowadzący działalność gospodarczą pod firmą zmist (a Polish sole proprietorship / JDG)';
export const JURISDICTION = 'Poland';
export const CONTACT_EMAIL = (
  <a href="mailto:support@zmist.co" className="text-blue-600 dark:text-blue-400 hover:underline">
    support@zmist.co
  </a>
);
export const REGISTERED_ADDRESS = null;
export const MIN_AGE = '16';

export function H2({ children }: { children: ReactNode }) {
  return <h2 className="text-xl font-bold mt-8 mb-2 first:mt-0">{children}</h2>;
}
export function H3({ children }: { children: ReactNode }) {
  return <h3 className="text-[15px] font-semibold mt-5 mb-1.5">{children}</h3>;
}
export function P({ children }: { children: ReactNode }) {
  return <p className="text-[15px] leading-relaxed text-black/70 dark:text-white/70 mb-3">{children}</p>;
}
export function Ul({ children }: { children: ReactNode }) {
  return <ul className="list-disc pl-5 text-[15px] leading-relaxed text-black/70 dark:text-white/70 mb-3 flex flex-col gap-1">{children}</ul>;
}
export function A({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-blue-600 dark:text-blue-400 hover:underline">
      {children}
    </Link>
  );
}
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto mb-3 -mx-1">
      <table className="w-full text-[14px] border-collapse">{children}</table>
    </div>
  );
}

export function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen w-full bg-[var(--color-bg)]">
      <header className="flex items-center gap-3 px-4 sm:px-6 py-4 max-w-2xl mx-auto">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img src="/zmist-logo.svg" alt="Zmist" className="w-8 h-8" />
          <span className="text-[15px] font-semibold">Zmist</span>
        </Link>
        <button
          onClick={() => navigate(-1)}
          className="ml-auto flex items-center gap-1 text-[14px] text-black/50 dark:text-white/50 hover:text-black/80 dark:hover:text-white/80"
        >
          <ChevronLeft size={16} /> Back
        </button>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pb-16 md:pb-20">
        <h1 className="text-3xl font-bold tracking-tight mt-2 mb-1">{title}</h1>
        <p className="font-mono text-[13px] text-black/40 dark:text-white/40 mb-6">Last updated {LEGAL_UPDATED}</p>
        <div className="h-px bg-black/10 dark:bg-white/10 mb-6" />
        {children}
      </main>
    </div>
  );
}
